import { Card, Col, DatePicker, Divider, Form, InputNumber, notification, Row, Select, Table, Typography } from "antd";
import ListTable from "common/ListTable";
import {
  MARINA_COVER_NOTE_BILL_ADD_ENDPOINT,
  MARINA_COVER_NOTE_BILL_EDIT_ENDPOINT,
  MARINA_COVER_NOTE_BILL_INFO_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

// ============================================================================
// CONSTANTS
// ============================================================================
const DEFAULT_CURRENCY = "BDT";
const PREMIUM_RATE = 0.0036; // 0.36%
const VAT_RATE = 0; // Default 0, user can change
const STAMP_AMOUNT = 0; // Default 0, user can change

// ============================================================================
// CALCULATION UTILITIES
// ============================================================================
const calculatePremium = (lcValue, conversionRate) => {
  console.log("lcValue", lcValue);
  console.log("conversionRate", conversionRate);
  const value = parseFloat(lcValue || 0);
  const rate = parseFloat(conversionRate);
  // If conversion rate is null, 0, NaN, or invalid, use default 120
  const finalRate = !rate || rate === 0 || isNaN(rate) ? 120 : rate;
  return value * finalRate * PREMIUM_RATE;
};

const calculateVAT = (premium) => {
  return premium * VAT_RATE;
};

const calculateStamp = () => {
  return STAMP_AMOUNT;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const MarinaCoverNoteBillForm = forwardRef((props, ref) => {
  const { form, isEdit, isCreate, editInfo, viewMode } = props;
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // FORM WATCHERS
  // --------------------------------------------------------------------------
  const percentageOfCommissionWatcher = Form.useWatch("percentage_of_commission", form);
  const fromDateWatcher = Form.useWatch("from_date", form);
  const toDateWatcher = Form.useWatch("to_date", form);
  const insuranceForWatcher = Form.useWatch("insurance_for", form);
  const insuranceCompanyIdWatcher = Form.useWatch("insurance_id", form);

  // --------------------------------------------------------------------------
  // STATE MANAGEMENT
  // --------------------------------------------------------------------------
  const [options, setOptions] = useState({
    insuranceCompanies: [],
    marineCoverNotes: [],
  });
  const [tableData, setTableData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // For checkbox selection
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // --------------------------------------------------------------------------
  // CALCULATION FUNCTIONS
  // --------------------------------------------------------------------------
  const recalculateTableItem = useCallback((item, changes = {}) => {
    // Extract values with fallbacks
    const lcValue = parseFloat(changes.lcValue !== undefined ? changes.lcValue : item.lc_value || 0);
    const cr = parseFloat(changes.conversionRate !== undefined ? changes.conversionRate : item.conversion_rate);
    // If conversion rate is null, 0, or NaN, use default 120
    const conversionRate = !cr || cr === 0 || isNaN(cr) ? 120 : cr;
    const commission = parseFloat(changes.commission !== undefined ? changes.commission : item.commission || 0);

    // Calculate premium
    const premium = calculatePremium(lcValue, conversionRate);

    // Calculate or use override values
    const vat = changes.vat !== undefined ? parseFloat(changes.vat || 0) : calculateVAT(premium);

    const stamp = changes.stamp !== undefined ? parseFloat(changes.stamp || 0) : calculateStamp();

    // Calculate totals
    const total = premium + vat + stamp;
    // Calculate payable value as Total * Percentage of Commission
    const payableValue = total * (commission / 100);
    console.log("premium", premium);

    return {
      ...item,
      lc_value: lcValue,
      conversion_rate: conversionRate,
      commission,
      premium,
      vat,
      stamp,
      total,
      payable_value: payableValue,
    };
  }, []);

  // --------------------------------------------------------------------------
  // API CALLS
  // --------------------------------------------------------------------------
  const searchInsuranceCompanies = useCallback(
    async (searchName = "", insuranceForOverride = null) => {
      // Determine insurance_for value: override param or form watcher
      const insuranceFor = insuranceForOverride || insuranceForWatcher;

      if (!insuranceFor) {
        // Do not search without insurance_for type selected
        return false;
      }

      setLoading(true);
      try {
        const params = {
          insurance_for: insuranceFor,
          ...(searchName ? { name: searchName } : {}),
        };

        const response = await getData(`/api/exim-cost/get_insurance_company_list`, null, params);

        if (response?.status === 200) {
          // Response structure: response.data.data or response.data (direct array)
          const insuranceCompanies = response?.data?.data || (Array.isArray(response?.data) ? response.data : []);
          setOptions((prev) => ({
            ...prev,
            insuranceCompanies,
          }));
        }
        return true;
      } catch (error) {
        console.error("Error searching insurance companies:", error);
        notification.error({
          message: "Error",
          description: "Failed to search insurance companies",
          placement: "topRight",
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [insuranceForWatcher],
  );

  const getMarineCoverNotes = useCallback(async () => {
    // Require all filter fields before calling API:
    // from_date, to_date, insurance_for, insurance_id, percentage_of_commission
    const hasPercentage =
      percentageOfCommissionWatcher !== undefined &&
      percentageOfCommissionWatcher !== null &&
      percentageOfCommissionWatcher !== "";

    if (!fromDateWatcher || !toDateWatcher || !insuranceForWatcher || !insuranceCompanyIdWatcher || !hasPercentage) {
      return;
    }

    // Don't fetch if we're in edit mode and already have the data from the API response
    const hasExistingData = isEdit && editInfo?.marine_details?.length > 0 && tableData.length > 0;

    if (hasExistingData) {
      return;
    }

    setLoading(true);
    setFetchError(null);
    try {
      const params = {
        from_date: moment(fromDateWatcher).format("YYYY-MM-DD"),
        to_date: moment(toDateWatcher).format("YYYY-MM-DD"),
        insurance_for: insuranceForWatcher,
        insurance_id: insuranceCompanyIdWatcher,
        mode: isCreate ? "create" : "edit",
      };

      const response = await getData(MARINA_COVER_NOTE_BILL_INFO_ENDPOINT, null, params);

      if (response?.status === 200) {
        const notes = response?.data?.data || [];

        if (notes.length === 0) {
          notification.info({
            message: "No Data Found",
            description: "No marine cover notes found for the selected criteria",
            placement: "topRight",
          });
          setTableData([]);
        } else {
          // Create detailed formatted notes for better display
          const formattedNotes = notes.map((note) => {
            return {
              ...note,
              id: note.id,
              marine_cover_note_number: note.marine_cover_note_number || note.id,
              name:
                note.marine_cover_note_number ||
                note.insurance_cover_note ||
                note.insurence_cover_note ||
                note.cover_note_number ||
                `Note #${note.id}`,
              displayName: note.insurance_cover_note || note.insurence_cover_note,
              conversion_rate: (() => {
                const cr = parseFloat(note.conversion_rate ?? note.b2blc_info?.conversion_rate);
                return !cr || cr === 0 || isNaN(cr) ? 120 : cr;
              })(),
            };
          });

          setOptions((prev) => ({
            ...prev,
            marineCoverNotes: formattedNotes,
          }));

          // Note: Row selection in edit mode is handled in fetchInitialData
        }
      }
    } catch (error) {
      console.error("Error fetching marine cover notes:", error);
      notification.error({
        message: "Error",
        description: "Failed to load marine cover notes",
        placement: "topRight",
      });
      setFetchError("Could not load this list.");
      setTableData([]);
      setSelectedRowKeys([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromDateWatcher,
    toDateWatcher,
    insuranceForWatcher,
    insuranceCompanyIdWatcher,
    percentageOfCommissionWatcher,
    isEdit,
    editInfo?.marine_details?.length,
    isCreate,
  ]);

  // --------------------------------------------------------------------------
  // INITIAL DATA LOADING
  // --------------------------------------------------------------------------
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      if (isEdit && editInfo) {
        form.setFieldsValue({
          from_date: editInfo.from_date ? moment(editInfo.from_date) : null,
          to_date: editInfo.to_date ? moment(editInfo.to_date) : null,
          insurance_for: editInfo.insurance_for || null,
          percentage_of_commission: editInfo.percentage_of_commission,
        });

        // Load insurance companies for the existing insurance_for type in edit mode
        if (editInfo.insurance_for) {
          await searchInsuranceCompanies("", editInfo.insurance_for);

          // If the company from editInfo is not in the loaded list, add it
          if (editInfo.company && editInfo.insurance_id) {
            setOptions((prev) => {
              const companyExists = prev.insuranceCompanies.some((company) => company.id === editInfo.company.id);

              if (!companyExists) {
                return {
                  ...prev,
                  insuranceCompanies: [...prev.insuranceCompanies, editInfo.company],
                };
              }
              return prev;
            });
          }
        }

        // Handle marine details if they exist in the response
        if (editInfo?.marine_details && Array.isArray(editInfo.marine_details)) {
          // Create a map of saved details for easy lookup
          const savedDetailsMap = {};
          editInfo.marine_details.forEach((detail) => {
            const key = detail.marine_cover_note_number;
            savedDetailsMap[key] = {
              vat: parseFloat(detail.vat || 0),
              stamp: parseFloat(detail.stamp || 0),
              premium: parseFloat(detail.premium || 0),
              total: parseFloat(detail.total || 0),
              payable_value: parseFloat(detail.payable_value || 0),
            };
          });

          // Fetch ALL available marine cover notes for the date range
          const params = {
            from_date: editInfo.from_date ? moment(editInfo.from_date).format("YYYY-MM-DD") : null,
            to_date: editInfo.to_date ? moment(editInfo.to_date).format("YYYY-MM-DD") : null,
            ...(editInfo.insurance_for && {
              insurance_for: editInfo.insurance_for,
            }),
            ...(editInfo.insurance_id && {
              insurance_id: editInfo.insurance_id,
            }),
            mode: isCreate ? "create" : "edit",
          };

          const response = await getData(MARINA_COVER_NOTE_BILL_INFO_ENDPOINT, null, params);

          if (response?.data?.data) {
            const commission = parseFloat(editInfo.percentage_of_commission || 0);

            // Process ALL available notes and merge with saved data
            const isExport = editInfo.insurance_for === "export";
            const allNotes = response.data.data.map((note, index) => {
              const noteKey = note.marine_cover_note_number || note.id;

              // For export: use invoice_value, for import: use b2blc_info.total_lc_value
              const lcValue = isExport
                ? parseFloat(note.invoice_value || 0)
                : parseFloat(note.b2blc_info?.total_lc_value || 0);

              const cr = parseFloat(note.conversion_rate ?? note.b2blc_info?.conversion_rate);
              const conversionRate = !cr || cr === 0 || isNaN(cr) ? 120 : cr;

              // Check if this note has saved data
              const savedData = savedDetailsMap[noteKey];

              const baseItem = {
                key: noteKey,
                id: noteKey,
                sl_no: index + 1,
                insurance_cover_note: note.insurance_cover_note || note.insurence_cover_note || note.name || "Auto",
                // Store both import and export data
                b2blc_info: note.b2blc_info || {},
                invoice_number: note.invoice_number,
                invoice_value: note.invoice_value,
                sales_contract_info: note.sales_contract_info || {},
                lc_value: lcValue,
                conversion_rate: conversionRate,
                is_export: isExport,
                amendment_value: note.amendment_value || 0,
              };

              // Always recalculate premium/total/payable with corrected conversion rate
              // But preserve VAT and Stamp from saved data
              return recalculateTableItem(baseItem, {
                lcValue,
                conversionRate,
                commission,
                vat: savedData?.vat || 0,
                stamp: savedData?.stamp || 0,
              });
            });

            setTableData(allNotes);

            // Set marine cover notes in options
            setOptions((prev) => ({
              ...prev,
              marineCoverNotes: response.data.data,
            }));

            // Auto-select the rows that were previously saved
            const savedKeys = allNotes.filter((note) => savedDetailsMap[note.key]).map((note) => note.key);

            // Use setTimeout to ensure table is rendered before selecting
            setTimeout(() => {
              setSelectedRowKeys(savedKeys);
            }, 100);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      notification.error({
        message: "Error",
        description: "Failed to load initial data",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  }, [isEdit, editInfo, form, searchInsuranceCompanies, recalculateTableItem, isCreate]);

  // --------------------------------------------------------------------------
  // EFFECTS
  // --------------------------------------------------------------------------
  // Initial load
  useEffect(() => {
    fetchInitialData();

    // Only clear marineCoverNotes if NOT in edit mode
    // This prevents clearing the options we set in fetchInitialData
    if (!isEdit) {
      setOptions((prev) => ({
        ...prev,
        marineCoverNotes: [],
      }));
    }
  }, [fetchInitialData, isEdit]);

  // Filter criteria changes
  useEffect(() => {
    // For edit mode, first load should preserve the data
    const isFirstLoadInEditMode = isEdit && editInfo?.marine_details?.length > 0 && tableData.length > 0;

    // Skip clearing if this is the first load in edit mode with data
    if (
      !isFirstLoadInEditMode &&
      (fromDateWatcher ||
        toDateWatcher ||
        insuranceForWatcher ||
        insuranceCompanyIdWatcher ||
        percentageOfCommissionWatcher)
    ) {
      setOptions((prev) => ({
        ...prev,
        marineCoverNotes: [],
      }));
      setTableData([]);
      setSelectedRowKeys([]);
    }

    const hasPercentage =
      percentageOfCommissionWatcher !== undefined &&
      percentageOfCommissionWatcher !== null &&
      percentageOfCommissionWatcher !== "";

    if (fromDateWatcher && toDateWatcher && insuranceForWatcher && insuranceCompanyIdWatcher && hasPercentage) {
      // In edit mode with existing data, don't re-fetch on first load
      if (!isFirstLoadInEditMode) {
        getMarineCoverNotes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromDateWatcher,
    toDateWatcher,
    insuranceForWatcher,
    insuranceCompanyIdWatcher,
    percentageOfCommissionWatcher,
    getMarineCoverNotes,
    isEdit,
  ]);

  // Load insurance companies when insurance_for type changes
  useEffect(() => {
    // Reset insurance company and clear related data when insurance_for changes
    form.resetFields(["insurance_id"]);
    setOptions((prev) => ({
      ...prev,
      insuranceCompanies: [],
      marineCoverNotes: [],
    }));
    setTableData([]);
    setSelectedRowKeys([]);

    // Only load insurance companies if insurance_for is selected
    if (insuranceForWatcher) {
      searchInsuranceCompanies("", insuranceForWatcher);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceForWatcher]);

  // Set insurance_id in edit mode when company is available in options
  useEffect(() => {
    if (isEdit && editInfo?.insurance_id && editInfo?.company) {
      const companyExists = options.insuranceCompanies.some((company) => company.id === editInfo.insurance_id);

      if (companyExists) {
        const currentValue = form.getFieldValue("insurance_id");
        if (currentValue !== editInfo.insurance_id) {
          form.setFieldsValue({
            insurance_id: editInfo.insurance_id,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.insuranceCompanies, isEdit, editInfo?.insurance_id]);

  // Load ALL Marine Cover Notes into table when date range and insurance company are selected
  useEffect(() => {
    // Skip updating if we're in edit mode with existing data
    // This prevents overwriting the data loaded from the API
    const isEditWithData = isEdit && editInfo?.marine_details?.length > 0 && tableData.length > 0;

    if (isEditWithData) {
      return;
    }

    if (!options.marineCoverNotes.length) {
      return;
    }

    // Load ALL marine cover notes into the table
    const processedNotes = options.marineCoverNotes.map((note, index) => {
      // Handle both import and export types
      const isExport = insuranceForWatcher === "export";

      // For export: use invoice_value, invoice_number, sales_contract_info
      // For import: use b2blc_info.total_lc_value, b2blc_info.b2b_lc_number, b2blc_info.sale_contract_info
      const lcValue = isExport ? parseFloat(note.invoice_value || 0) : parseFloat(note.b2blc_info?.total_lc_value || 0);

      const cr = parseFloat(note.conversion_rate ?? note.b2blc_info?.conversion_rate);
      const conversionRate = !cr || cr === 0 || isNaN(cr) ? 120 : cr;
      const commission = parseFloat(percentageOfCommissionWatcher || 0);

      const baseItem = {
        key: note.marine_cover_note_number || note.id,
        id: note.marine_cover_note_number || note.id,
        sl_no: index + 1,
        insurance_cover_note: note.insurance_cover_note || note.insurence_cover_note || note.name || "Auto",
        // Store both import and export data
        b2blc_info: note.b2blc_info || {},
        invoice_number: note.invoice_number,
        invoice_value: note.invoice_value,
        sales_contract_info: note.sales_contract_info || {},
        lc_value: lcValue,
        conversion_rate: conversionRate,
        is_export: isExport,
        amendment_value: note.amendment_value || 0,
      };

      return recalculateTableItem(baseItem, {
        lcValue,
        conversionRate,
        commission,
        vat: note.vat || 0, // Use existing VAT if available
        stamp: note.stamp || 0, // Use existing stamp if available
      });
    });

    if (processedNotes.length > 0) {
      setTableData(processedNotes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.marineCoverNotes, percentageOfCommissionWatcher, insuranceForWatcher, isEdit]);

  // Commission percentage changes - recalculate payable values only
  useEffect(() => {
    if (percentageOfCommissionWatcher !== undefined && tableData.length > 0) {
      const commission = parseFloat(percentageOfCommissionWatcher || 0);
      const updatedData = tableData.map((item) => {
        // Only update commission, preserve all other values including VAT and Stamp
        return recalculateTableItem(item, {
          lcValue: item.lc_value,
          conversionRate: item.conversion_rate,
          commission,
          vat: item.vat, // Preserve existing VAT
          stamp: item.stamp, // Preserve existing Stamp
        });
      });
      setTableData(updatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentageOfCommissionWatcher]);

  // --------------------------------------------------------------------------
  // CALCULATED VALUES
  // --------------------------------------------------------------------------
  const grandTotal = useMemo(() => {
    // Only sum total values for checked rows
    return tableData
      .filter((item) => selectedRowKeys.includes(item.key))
      .reduce((sum, item) => {
        return sum + (parseFloat(item.total) || 0);
      }, 0);
  }, [tableData, selectedRowKeys]);

  const grandPayableTotal = useMemo(() => {
    // Only sum payable values for checked rows
    return tableData
      .filter((item) => selectedRowKeys.includes(item.key))
      .reduce((sum, item) => {
        return sum + (parseFloat(item.payable_value) || 0);
      }, 0);
  }, [tableData, selectedRowKeys]);

  // --------------------------------------------------------------------------
  // FORM SUBMISSION
  // --------------------------------------------------------------------------
  const handleSubmit = useCallback(
    async (values) => {
      try {
        // Validation
        if (
          !values.from_date ||
          !values.to_date ||
          !values.insurance_id ||
          values.percentage_of_commission === undefined
        ) {
          notification.error({
            message: "Error",
            description: "Please fill in all required fields",
            placement: "topRight",
          });
          return;
        }

        if (selectedRowKeys.length === 0) {
          notification.error({
            message: "Error",
            description: "Please select at least one marine cover note by checking the checkbox",
            placement: "topRight",
          });
          return;
        }

        // Only include checked rows in the payload
        const checkedRows = tableData.filter((item) => selectedRowKeys.includes(item.key));

        // Validate that all selected rows have VAT and Stamp values
        const invalidRows = checkedRows.filter((row) => {
          const vat = parseFloat(row.vat);
          const stamp = parseFloat(row.stamp);
          return isNaN(vat) || vat < 0 || isNaN(stamp) || stamp < 0;
        });

        if (invalidRows.length > 0) {
          notification.error({
            message: "Validation Error",
            description: "All selected rows must have valid VAT and Stamp values (greater than or equal to 0)",
            placement: "topRight",
          });
          return;
        }

        const details = checkedRows.map((item) => ({
          marine_cover_note_number: item.id,
          conversion_rate: item.conversion_rate,
          premium: item.premium,
          vat: item.vat,
          stamp: item.stamp,
          total: item.total,
          payable_value: item.payable_value,
        }));

        const body = {
          from_date: moment(values.from_date).format("YYYY-MM-DD"),
          to_date: moment(values.to_date).format("YYYY-MM-DD"),
          insurance_for: values.insurance_for,
          insurance_id: values.insurance_id,
          percentage_of_commission: parseFloat(values.percentage_of_commission || 0),
          grand_total: grandTotal,
          details,
        };

        // For edit mode, include the ID in the body as well
        if (isEdit && editInfo?.id) {
          body.id = editInfo.id;
        }

        const query =
          isEdit && editInfo?.id
            ? `${MARINA_COVER_NOTE_BILL_EDIT_ENDPOINT}/${editInfo.id}`
            : MARINA_COVER_NOTE_BILL_ADD_ENDPOINT;

        const response = await postData(query, body);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: response?.message || `Marine Cover Note Bill ${isEdit ? "updated" : "created"} successfully`,
            placement: "topRight",
          });
          navigate("/import-export-expense-tracking/marine-cover-note-bill");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        notification.error({
          message: "Error",
          description: "Failed to save Marine Cover Note Bill: " + (error.message || "Unknown error"),
          placement: "topRight",
        });
      }
    },
    [isEdit, editInfo, navigate, tableData, grandTotal, selectedRowKeys],
  );

  // --------------------------------------------------------------------------
  // HELPER FUNCTIONS
  // --------------------------------------------------------------------------

  const handleTableValueChange = useCallback(
    (record, field, value) => {
      const newData = [...tableData];
      const index = newData.findIndex((item) => item.key === record.key);

      if (index > -1) {
        const item = newData[index];
        const commission = parseFloat(percentageOfCommissionWatcher || 0);

        // For VAT and Stamp changes, preserve other values
        const changes = {
          commission,
          [field]: parseFloat(value || 0),
          conversionRate: item.conversion_rate,
          lcValue: item.lc_value,
        };

        // Preserve other editable values
        if (field !== "vat") changes.vat = item.vat;
        if (field !== "stamp") changes.stamp = item.stamp;

        newData[index] = recalculateTableItem(item, changes);

        setTableData(newData);
      }
    },
    [tableData, percentageOfCommissionWatcher, recalculateTableItem],
  );

  // --------------------------------------------------------------------------
  // TABLE COLUMNS
  // --------------------------------------------------------------------------
  const tableColumns = useMemo(
    () => [
      {
        title: "SL No",
        dataIndex: "sl_no",
        key: "sl_no",
        width: "5%",
        fixed: "left",
        render: (text, record, index) => index + 1,
      },
      {
        title: "Marine Cover Note Number",
        dataIndex: "insurance_cover_note",
        key: "insurance_cover_note",
        width: "10%",
        fixed: "left",
      },
      {
        title: insuranceForWatcher === "export" ? "Invoice Number" : "LC Number",
        dataIndex: insuranceForWatcher === "export" ? "invoice_number" : ["b2blc_info", "b2b_lc_number"],
        key: insuranceForWatcher === "export" ? "invoice_number" : "lc_number",
        width: "10%",
        fixed: "left",
        render: (value, record) => {
          if (insuranceForWatcher === "export") {
            return record.invoice_number || "-";
          }
          return record.b2blc_info?.b2b_lc_number || "-";
        },
      },
      {
        title: "SC Number",
        dataIndex:
          insuranceForWatcher === "export"
            ? ["sales_contract_info", "reference_no"]
            : ["b2blc_info", "sale_contract_info", "reference_no"],
        key: "sc_number",
        width: "10%",
        render: (value, record) => {
          if (insuranceForWatcher === "export") {
            return record.sales_contract_info?.reference_no || "-";
          }
          return record.b2blc_info?.sale_contract_info?.reference_no || "-";
        },
      },
      {
        title: insuranceForWatcher === "export" ? "Invoice Value" : "LC Value",
        dataIndex: "amendment_value",
        key: "amendment_value",
        width: "10%",
        align: "right",
        render: (value, record) => {
          return parseFloat(record.amendment_value || 0).toFixed(2);
        },
      },
      {
        title: "Conversion Rate",
        dataIndex: "conversion_rate",
        key: "conversion_rate",
        width: "10%",
        align: "right",
        render: (value) => parseFloat(value || 0).toFixed(2),
      },
      {
        title: `Premium (${PREMIUM_RATE * 100}%)`,
        dataIndex: "premium",
        key: "premium",
        width: "10%",
        align: "right",
        render: (value) => parseFloat(value || 0).toFixed(2),
      },
      {
        title: "VAT *",
        dataIndex: "vat",
        key: "vat",
        width: "10%",
        align: "right",
        render: (value, record) =>
          viewMode ? (
            parseFloat(record.vat).toFixed(2)
          ) : (
            <InputNumber
              style={{
                width: "100%",
                textAlign: "right",
                borderColor:
                  selectedRowKeys.includes(record.key) && (value === undefined || value === null) ? "red" : undefined,
              }}
              min={0}
              step={0.01}
              value={record.vat}
              onChange={(value) => handleTableValueChange(record, "vat", value)}
              disabled={!selectedRowKeys.includes(record.key)}
              placeholder="Enter VAT"
            />
          ),
      },
      {
        title: "Stamp *",
        dataIndex: "stamp",
        key: "stamp",
        width: "10%",
        align: "right",
        render: (value, record) =>
          viewMode ? (
            parseFloat(record.stamp).toFixed(2)
          ) : (
            <InputNumber
              style={{
                width: "100%",
                textAlign: "right",
                borderColor:
                  selectedRowKeys.includes(record.key) && (value === undefined || value === null) ? "red" : undefined,
              }}
              min={0}
              step={0.01}
              value={record.stamp}
              onChange={(value) => handleTableValueChange(record, "stamp", value)}
              disabled={!selectedRowKeys.includes(record.key)}
              placeholder="Enter Stamp"
            />
          ),
      },
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        width: "10%",
        align: "right",
        render: (value) => parseFloat(value || 0).toFixed(2),
      },
      {
        title: "Payable Value",
        dataIndex: "payable_value",
        key: "payable_value",
        width: "10%",
        align: "right",
        render: (value) => parseFloat(value || 0).toFixed(2),
      },
    ],
    [viewMode, handleTableValueChange, selectedRowKeys, insuranceForWatcher],
  );

  // --------------------------------------------------------------------------
  // EXPOSE FORM INSTANCE
  // --------------------------------------------------------------------------
  useImperativeHandle(ref, () => ({ form }), [form]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <div className="containt-body">
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={true}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="From Date"
                name="from_date"
                rules={[{ required: true, message: "Please select a from date" }]}
              >
                <DatePicker style={{ width: "100%" }} disabled={viewMode} placeholder="Select From Date" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="To Date"
                name="to_date"
                rules={[{ required: true, message: "Please select a to date" }]}
              >
                <DatePicker style={{ width: "100%" }} disabled={viewMode} placeholder="Select To Date" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Insurance For"
                name="insurance_for"
                rules={[
                  {
                    required: true,
                    message: "Please select insurance for type",
                  },
                ]}
              >
                <Select
                  placeholder="Select Insurance For"
                  disabled={viewMode}
                  options={[
                    { label: "Import", value: "import" },
                    { label: "Export", value: "export" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Insurance Company"
                name="insurance_id"
                rules={[
                  {
                    required: true,
                    message: "Please select an insurance company",
                  },
                ]}
              >
                <Select
                  key={`insurance-company-${insuranceForWatcher || "none"}`}
                  placeholder="Select or search for insurance company"
                  disabled={viewMode || !insuranceForWatcher}
                  showSearch
                  filterOption={false}
                  onSearch={(value) => value && insuranceForWatcher && searchInsuranceCompanies(value)}
                  options={options.insuranceCompanies.map((company) => ({
                    label: company.name,
                    value: company.id,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Percentage of Payable (%)"
                name="percentage_of_commission"
                rules={[
                  {
                    required: true,
                    message: "Please enter Percentage of Payable",
                  },
                ]}
                initialValue={1}
                tooltip="Enter percentage value (e.g., 50 for 50%)"
              >
                <InputNumber
                  placeholder="Enter percentage (e.g., 50)"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">
            Marine Cover Notes {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length} Selected)` : ""}
          </Divider>
          <ListTable
            bordered
            error={fetchError}
            onRetry={() => getMarineCoverNotes()}
            dataSource={tableData}
            columns={tableColumns}
            pagination={false}
            loading={loading}
            scroll={{ x: "max-content" }}
            emptyText="No records to show yet."
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: selectedRowKeys,
              onChange: (selectedKeys) => {
                setSelectedRowKeys(selectedKeys);
              },
              getCheckboxProps: (record) => ({
                disabled: viewMode, // Disable checkbox in view mode
              }),
            }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={10} align="right">
                    <Text strong>Grand Total:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>
                      {grandTotal.toFixed(2)} {DEFAULT_CURRENCY}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>
                      {grandPayableTotal.toFixed(2)} {DEFAULT_CURRENCY}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Form>
      </Card>
    </div>
  );
});

MarinaCoverNoteBillForm.displayName = "MarinaCoverNoteBillForm";

export default MarinaCoverNoteBillForm;
